import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Container, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Print } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { groupBy, repairRequestDbStatuses } from "../../../_helpers";
import { apps, userAccountRoleConstants } from "../../../_constants";
import { getDashboardData } from "../../../apis/aftercareApis";
import { getRepairRequestCarryOutAsTypeList } from "../../../apis/surveyingApis";
import DashboardFilters from "./DashboardFilters";
import DashboardKPITiles from "./DashboardKPITiles";
import DashboardCharts from "./DashboardCharts";

const getDefaultDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { startDate: fmt(start), endDate: fmt(end) };
};

const AFTERCARE_ROLES = [
  userAccountRoleConstants.propertyManager,
  userAccountRoleConstants.aftercareEmployee,
  userAccountRoleConstants.siteManager,
];
const CQ_ROLES = [userAccountRoleConstants.siteManager];

const DashboardPage = (props) => {
  const { history, app: appProp } = props;
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const currentApp = appProp || apps.aftercare;
  const allowedRoles = currentApp === apps.constructionQuality ? CQ_ROLES : AFTERCARE_ROLES;
  const { all = [], loading: buildingLoading } = useSelector(
    (state) => state.buildings
  );

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeList, setTypeList] = useState([]);

  // Derive projects available to allowed roles
  const dashboardBuildings = useMemo(
    () =>
      all.filter(
        (a) =>
          a.roles && a.roles.some((r) => allowedRoles.includes(r))
      ),
    [all, allowedRoles]
  );

  const projects = useMemo(() => {
    const grouped = groupBy(dashboardBuildings, "projectId");
    return Object.entries(grouped).map(([projectId, buildings]) => ({
      id: projectId,
      name: buildings[0].projectName || buildings[0].projectNo || projectId,
    }));
  }, [dashboardBuildings]);

  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  const [filters, setFilters] = useState({
    projectIds: [],
    typeId: "",
    duration: "monthly",
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
  });

  // Set default project selection when buildings load
  useEffect(() => {
    if (projects.length > 0 && filters.projectIds.length === 0) {
      setFilters((prev) => ({
        ...prev,
        projectIds: projects.map((p) => p.id),
      }));
    }
  }, [projects]);

  // Redirect if user has no qualifying role
  useEffect(() => {
    if (buildingLoading) return;
    const hasRole = all.some(
      (a) => a.roles && a.roles.some((r) => allowedRoles.includes(r))
    );
    if (!hasRole) {
      history.push("/home");
    }
  }, [all, buildingLoading, allowedRoles]);

  // Fetch type list
  useEffect(() => {
    getRepairRequestCarryOutAsTypeList()
      .then((res) => setTypeList(res.data || []))
      .catch(console.error);
  }, []);

  // Fetch dashboard data when filters change
  const fetchData = useCallback(async () => {
    if (!projects.length) return;
    setLoading(true);
    try {
      const projectIds = filters.projectIds.length > 0
        ? filters.projectIds
        : projects.map((p) => p.id);
      const payload = {
        app: currentApp,
        projectIds,
        typeId: filters.typeId || null,
        duration: filters.duration,
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      const res = await getDashboardData(payload);
      setDashboardData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, projects]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTileClick = useCallback((tileKey) => {
    const rrDbStatuses = repairRequestDbStatuses(i18n);
    const statusMap = {
      newCount: [rrDbStatuses.NEW],
      inProgressCount: [rrDbStatuses.IN_PROGRESS],
      completedCount: [rrDbStatuses.COMPLETED],
      rejectedCount: [rrDbStatuses.TURNED_DOWN],
    };
    const status = statusMap[tileKey] || [];
    const projectIds = filters.projectIds.length > 0
      ? filters.projectIds
      : projects.map((p) => p.id);
    let pathname = "/nazorg/meldingen";
    if (currentApp === apps.constructionQuality) {
      const firstBuilding = dashboardBuildings.find((b) => projectIds.includes(b.projectId));
      pathname = `/werk/${firstBuilding?.projectNo}/kwaliteitsborging`;
    }
    const state = {
      filter: tileKey,
      projectIds,
      status,
    };
    if (filters.typeId) {
      const typeName = typeList.find((tp) => tp.id === filters.typeId)?.name;
      if (typeName) state.carryOutAsType = typeName;
    }
    if (filters.startDate) state.dateFrom = filters.startDate;
    if (filters.endDate) state.dateTo = filters.endDate;
    history.push({ pathname, state });
  }, [filters, projects, dashboardBuildings, typeList, currentApp, history, i18n]);

  const printRef = useRef(null);

  const filterChipLabels = useMemo(() => {
    const chips = [];
    const selectedNames = projects
      .filter((p) => filters.projectIds.includes(p.id))
      .map((p) => p.name);
    if (selectedNames.length && selectedNames.length < projects.length) {
      chips.push(`${t("dashboard.filter.project")}: ${selectedNames.join(", ")}`);
    }
    if (filters.typeId) {
      const typeName = typeList.find((tp) => tp.id === filters.typeId)?.name;
      if (typeName) chips.push(`${t("dashboard.filter.type")}: ${typeName}`);
    }
    if (filters.duration && filters.duration !== "custom") {
      chips.push(t(`dashboard.duration.${filters.duration}`));
    }
    if (filters.startDate || filters.endDate) {
      chips.push(`${filters.startDate || "..."} — ${filters.endDate || "..."}`);
    }
    return chips;
  }, [filters, projects, typeList, t]);

  // Iframe-based print: clones dashboard content into a clean iframe document
  const handlePrint = useCallback(() => {
    const content = printRef.current;
    if (!content) return;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.left = "-9999px";
    iframe.style.top = "-9999px";
    iframe.style.width = "0";
    iframe.style.height = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write("<!DOCTYPE html><html><head></head><body></body></html>");
    doc.close();

    // Inline all CSS rules from loaded stylesheets (works in both dev and production)
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        const rules = Array.from(sheet.cssRules || [])
          .map((rule) => rule.cssText)
          .filter((text) => !/@page\s*\{/.test(text))
          .join("\n");
        if (rules) {
          const style = doc.createElement("style");
          style.textContent = rules;
          doc.head.appendChild(style);
        }
      } catch (e) {
        // Cross-origin stylesheets can't be read; copy the link instead
        if (sheet.href) {
          const link = doc.createElement("link");
          link.rel = "stylesheet";
          link.href = sheet.href;
          doc.head.appendChild(link);
        }
      }
    });

    // Add print-specific styles
    const printStyle = doc.createElement("style");
    printStyle.textContent = `
      body { margin: 0; padding: 16px; background: #fff; }
      .print-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #e0e0e0; }
      .print-chip {
        display: inline-block;
        padding: 2px 10px;
        border: 1px solid #1976d2;
        border-radius: 16px;
        font-size: 12px;
        color: #1976d2;
        font-family: "Roboto", sans-serif;
      }
      .MuiPaper-root {
        border: 1px solid #e0e0e0;
        box-shadow: none !important;
      }
    `;
    doc.head.appendChild(printStyle);

    // Add filter summary chips
    if (filterChipLabels.length > 0) {
      const chipsDiv = doc.createElement("div");
      chipsDiv.className = "print-chips";
      filterChipLabels.forEach((label) => {
        const chip = doc.createElement("span");
        chip.className = "print-chip";
        chip.textContent = label;
        chipsDiv.appendChild(chip);
      });
      doc.body.appendChild(chipsDiv);
    }

    // Clone the dashboard content
    const clone = content.cloneNode(true);
    doc.body.appendChild(clone);

    // Wait for SVG charts to render, then print
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 300);
  }, [filterChipLabels]);

  // Intercept Ctrl+P / Cmd+P to use iframe print
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handlePrint]);

  if (buildingLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box className={classes.page}>
      <Container maxWidth="xl" className={classes.root}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {t("dashboard.title")}
          </Typography>
          <Tooltip title={t("dashboard.print")}>
            <IconButton onClick={handlePrint}>
              <Print />
            </IconButton>
          </Tooltip>
        </Stack>
        <DashboardFilters
          projects={projects}
          typeList={typeList}
          filters={filters}
          onFiltersChange={setFilters}
        />
        <div ref={printRef}>
          <DashboardKPITiles data={dashboardData} loading={loading} onTileClick={handleTileClick} />
          <DashboardCharts data={dashboardData} loading={loading} duration={filters.duration} />
        </div>
      </Container>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  page: {
    backgroundColor: theme.palette.grey[100],
    minHeight: "100%",
    width: "100%",
    overflowX: "hidden",
  },
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
}));

export default DashboardPage;
